import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand, GripHorizontal } from "lucide-react";
import { GALLERY, FILTERS } from "@/constants/site";
import HoloCard from "@/components/HoloCard";
import { useHolo } from "@/lib/holo";

const EASE = [0.16, 1, 0.3, 1];

export default function Gallery() {
  const [filter, setFilter] = useState("Tous");
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(0);
  const [thumbRatio, setThumbRatio] = useState(0.25);
  const trackRef = useRef(null);
  const barRef = useRef(null);
  const dragging = useRef(false);
  const holo = useHolo();

  const items = filter === "Tous" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.getBoundingClientRect().width + 20 : 400;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth + 4) {
      setProgress(1);
      setThumbRatio(1);
      return;
    }
    setProgress(el.scrollLeft / (el.scrollWidth - el.clientWidth));
    setThumbRatio(el.clientWidth / el.scrollWidth);
  };

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    setProgress(0);
  }, [filter]);

  useEffect(() => {
    onScroll();
    window.addEventListener("resize", onScroll);
    return () => window.removeEventListener("resize", onScroll);
  }, [filter, items.length]);

  const setScrollFromPointer = (e) => {
    const bar = barRef.current;
    const el = trackRef.current;
    if (!bar || !el) return;
    const r = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    el.scrollLeft = ratio * (el.scrollWidth - el.clientWidth);
  };

  const onBarPointerDown = (e) => {
    dragging.current = true;
    barRef.current?.setPointerCapture?.(e.pointerId);
    setScrollFromPointer(e);
  };

  const onBarPointerMove = (e) => {
    if (!dragging.current) return;
    setScrollFromPointer(e);
  };

  const endBarDrag = (e) => {
    dragging.current = false;
    barRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const atStart = progress <= 0.01;
  const atEnd = progress >= 0.99;

  return (
    <section id="galerie" className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-10 flex flex-col gap-6 md:mb-12 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">01 — Galerie</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Créations <span className="text-holo">uniques</span>, 100% personnalisées
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <p className="hidden max-w-xs text-sm leading-relaxed text-[#a09bb5] md:block">
              Faites défiler — touchez une carte pour voir son effet holographique.
            </p>
            <div className="ml-auto flex items-center gap-2.5">
              <button
                type="button"
                data-testid="gallery-carousel-prev"
                aria-label="Création précédente"
                onClick={() => scrollByCard(-1)}
                disabled={atStart}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-all duration-300 hover:border-cyan-400/60 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft size={19} />
              </button>
              <button
                type="button"
                data-testid="gallery-carousel-next"
                aria-label="Création suivante"
                onClick={() => scrollByCard(1)}
                disabled={atEnd}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-all duration-300 hover:border-cyan-400/60 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight size={19} />
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
          className="mb-8 flex flex-wrap gap-2.5"
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-300 sm:text-sm ${
                filter === f
                  ? "border-transparent bg-white text-[#08070d]"
                  : "border-white/15 text-[#a09bb5] hover:border-white/40 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </motion.div>
      </div>

      <div className="relative">
        <div
          ref={trackRef}
          data-testid="gallery-carousel"
          onScroll={onScroll}
          className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-2 md:px-8 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
        >
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.div
                layout
                key={item.id}
                data-card
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.55, ease: EASE, delay: Math.min(i, 3) * 0.07 }}
                className="shrink-0 snap-start"
              >
                <HoloCard item={item} onSelect={setSelected}>
                  <img
                    src={item.image}
                    alt={`${item.title} — ${item.subtitle}`}
                    loading="lazy"
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#08070d]/95 via-transparent to-transparent"
                    aria-hidden="true"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                    <div>
                      <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.25em] text-cyan-300">{item.category}</p>
                      <h3 className="font-display text-lg font-bold text-white sm:text-xl">{item.title}</h3>
                      <p className="text-xs text-[#a09bb5]">{item.subtitle}</p>
                    </div>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:opacity-100">
                      <Expand size={15} />
                    </span>
                  </div>
                </HoloCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="mx-auto mt-7 max-w-7xl px-5 md:px-8">
          <div
            ref={barRef}
            data-testid="gallery-carousel-bar"
            role="slider"
            aria-label="Faire défiler la galerie"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            onPointerDown={onBarPointerDown}
            onPointerMove={onBarPointerMove}
            onPointerUp={endBarDrag}
            onPointerCancel={endBarDrag}
            className="relative h-6 w-full cursor-grab touch-none select-none active:cursor-grabbing"
          >
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-400/50 via-fuchsia-500/50 to-amber-400/50"
                style={{ width: `${Math.min(100, progress * 100 + (thumbRatio * 100) / 2)}%` }}
              />
            </div>
            <div
              data-testid="gallery-carousel-thumb"
              className="btn-holo absolute top-1/2 flex h-7 -translate-y-1/2 items-center justify-center rounded-full shadow-[0_4px_16px_rgba(255,0,127,0.35)]"
              style={{
                left: `${progress * (100 - thumbRatio * 100)}%`,
                width: `${Math.max(thumbRatio * 100, 14)}%`,
                minWidth: "64px",
              }}
            >
              <GripHorizontal size={18} className="text-[#08070d]" />
            </div>
          </div>
          <p className="mt-1.5 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#6c6685]">
            Glissez la poignée pour parcourir la galerie
          </p>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050408]/95 p-4 backdrop-blur-md"
            data-testid="gallery-modal"
            onClick={() => setSelected(null)}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 16 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="relative max-h-[88vh] w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                ref={holo.innerRef}
                onPointerMove={holo.onPointerMove}
                data-testid="gallery-modal-holo"
                className="holo-inner holo-active rounded-2xl"
              >
                <img
                  src={selected.image}
                  alt={`${selected.title} — ${selected.subtitle}`}
                  className="max-h-[80vh] w-full rounded-2xl border border-white/15 object-contain"
                  draggable={false}
                />
                <div className="holo-foil" aria-hidden="true" />
              </div>
              <figcaption className="mt-4 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-cyan-300">{selected.category}</p>
                  <h3 className="font-display text-xl font-bold text-white">{selected.title}</h3>
                </div>
                <p className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-[#6c6685] sm:block">
                  Pièce unique — création originale
                </p>
              </figcaption>
              <button
                type="button"
                data-testid="gallery-modal-close"
                aria-label="Fermer"
                onClick={() => setSelected(null)}
                className="absolute -top-3 -right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-[#100e19] text-white transition-colors hover:bg-white hover:text-[#08070d]"
              >
                <X size={18} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
